/**
 * Rendu des blocs d'adresse des documents PDF (factures, devis, bons de
 * transport…).
 *
 * Pourquoi ce helper : les écrans construisaient les lignes par concaténation
 * nue — `street + " " + nr`, `cp + " " + place`, `region + " (" + country + ")"`
 * — sous un garde `if (this.addressbilling)`. Or les endpoints
 * /person/getaddress*details renvoient `[]` quand `operations.idaddressbilling`
 * / `idaddressdelivery` est perdu, et le front stocke alors `{}` : un objet
 * vide, donc truthy. Résultat imprimé sur une vraie facture client :
 *
 *     Rechnungsadresse:
 *     undefined undefined
 *     undefined undefined
 *     undefined (undefined)
 *
 * (facture EU3026_00530, Quappen Holzbau GmbH). Même piège qu'en SQL avec
 * `'x' || NULL = NULL` : un seul maillon manquant pollue toute la ligne.
 *
 * Règle : on ne concatène jamais un champ d'adresse sans le filtrer.
 */

/** Champs attendus par les endpoints /person/getaddress{billing,delivery}details. */
const PARTS = ['street', 'nr', 'cp', 'place', 'region', 'province', 'country']

const clean = (value) => {
  if (value === null || value === undefined) return ''
  const text = String(value).trim()
  // "undefined" / "null" en dur : déjà vus en base, ils ne valent pas mieux
  // qu'une valeur absente.
  if (!text || text === 'undefined' || text === 'null') return ''
  return text
}

/**
 * Normalise la réponse de /person/getaddress{billing,delivery}details.
 *
 * Renvoie TOUJOURS un objet dont tous les champs sont des chaînes : jamais
 * `undefined`, donc jamais d'`undefined` imprimé sur un document ni affiché
 * dans un template. À utiliser à chaque affectation de `addressbilling` /
 * `addressdelivery` — c'est le point unique où l'absence de donnée entre.
 *
 * Ne remplace pas le garde : un objet normalisé reste truthy même vide, il
 * faut tester `hasAddress()` avant d'imprimer un bloc.
 */
export function normalizeAddress (raw) {
  const source = raw && typeof raw === 'object' ? raw : {}
  const normalized = {}
  PARTS.forEach((name) => { normalized[name] = clean(source[name]) })
  // On conserve les champs hors-liste (id, latitude, longitude…) tels quels :
  // certains écrans les utilisent pour la carte ou l'e-factura.
  Object.keys(source).forEach((key) => {
    if (!(key in normalized)) normalized[key] = source[key]
  })
  return normalized
}

/**
 * Adapte la forme imbriquée renvoyée par Sequelize sur `people[].addresses[]`
 * (`{street, number, place: {postal_code, place_name, region, country: {name_en}}}`)
 * vers la forme plate utilisée ici. Les documents transport / réception lisaient
 * `addresses[0].place.country.name_en` en direct : un `place` ou un `country`
 * non chargé faisait planter le PDF, et un champ absent imprimait `undefined`.
 */
export function fromPersonAddress (raw) {
  if (!raw || typeof raw !== 'object') return null
  const place = raw.place || {}
  const country = place.country || {}
  return {
    street: raw.street,
    nr: raw.number,
    cp: place.postal_code,
    place: place.place_name,
    region: place.region,
    province: place.province,
    country: country.name_en
  }
}

/**
 * Une adresse n'est exploitable que si elle porte au moins la rue ou la
 * localité. Le code postal ou le pays seuls ne permettent ni de livrer, ni de
 * facturer.
 */
export function hasAddress (address) {
  if (!address || typeof address !== 'object') return false
  return Boolean(clean(address.street) || clean(address.place) || clean(address.cp))
}

/**
 * Lignes du bloc d'adresse, dans l'ordre d'impression, sans ligne vide :
 *   1. rue + numéro
 *   2. code postal + localité
 *   3. région, province (pays)
 * Renvoie [] si l'adresse n'est pas exploitable — l'appelant imprime alors son
 * propre libellé "n/a".
 */
export function addressLines (address) {
  if (!hasAddress(address)) return []

  const part = (name) => clean(address[name])
  const join = (names, separator) => names.map(part).filter(Boolean).join(separator)

  const lines = []

  const street = join(['street', 'nr'], ' ')
  if (street) lines.push(street)

  const city = join(['cp', 'place'], ' ')
  if (city) lines.push(city)

  const area = join(['region', 'province'], ', ')
  const country = part('country')
  if (area && country) lines.push(`${area} (${country})`)
  else if (area) lines.push(area)
  else if (country) lines.push(country)

  return lines
}

/** Même adresse sur une seule ligne (libellés courts, e-mails). */
export function addressOneLine (address) {
  return addressLines(address).join(', ')
}

/* ------------------------------------------------------------------------ *
 * Arrêts de transport — colonne `addresses` de /getroutetransportv{2,3}
 * ------------------------------------------------------------------------ */

/**
 * Découpe la chaîne plate renvoyée par le backend :
 *
 *   V2 : "<adresse>@<société> @<tel>@<tva>@<idlanguage>"   (4 '@')
 *   V3 : "<adresse>@<société> @<tel>"                      (2 '@')
 *
 * Renvoie TOUJOURS cinq chaînes, jamais `undefined` : les documents faisaient
 * `routetransport[i].addresses.split("@")` sans garde, et une seule ligne NULL
 * (concaténation SQL cassée par un maillon manquant) plantait la génération
 * entière — CMR du transport 234255, aviz 738. Les `.substring(0, 46)` en aval
 * plantaient de la même façon sur un champ absent.
 *
 * Ne remplace pas le garde : une ligne peut être parsée et rester inexploitable,
 * d'où `routeStopIssues()`.
 */
export function parseRouteAddress (raw) {
  const parts = String(raw === null || raw === undefined ? '' : raw).split('@')
  const at = (index) => {
    const value = clean(parts[index])
    // '-' est le remplissage du backend pour un champ absent : le neutraliser
    // ici évite d'imprimer "-" là où l'appelant veut son propre libellé.
    return value === '-' ? '' : value
  }
  const address = at(0)
  // Les documents impriment l'adresse sur deux lignes : « rue numéro » puis
  // « code_postal localité pays ». Le backend les sépare par ", ".
  const [street, ...rest] = address.split(', ')
  return {
    address,
    addressLine1: clean(street),
    addressLine2: clean(rest.join(', ')),
    company: at(1),
    phone: at(2),
    vat: at(3),
    language: at(4)
  }
}

/**
 * Même contenu que `parseRouteAddress`, sous la forme du tableau positionnel
 * `[adresse, société, téléphone, TVA, idlanguage]` attendu par les générateurs
 * PDF existants (`diviseuraddresscontact[1]`, `[4] == "2"`…).
 *
 * Les cinq cases sont TOUJOURS des chaînes : les `.substring(0, 46)` en aval ne
 * peuvent plus lever de TypeError. Un champ absent vaut "-", comme le
 * remplissage du backend.
 */
export function routeStopFields (raw) {
  const parsed = parseRouteAddress(raw)
  return [parsed.address, parsed.company, parsed.phone, parsed.vat, parsed.language]
    .map((value) => value || '-')
}

/** Libellés anglais des champs qui empêchent d'imprimer un document. */
const ROUTE_REQUIRED = [
  { field: 'address', label: 'delivery address' },
  { field: 'company', label: 'consignee name' }
]

/**
 * Ce qui manque à un arrêt pour qu'un CMR / aviz soit imprimable, en anglais.
 * Renvoie [] quand l'arrêt est exploitable. Seuls l'adresse et le nom du
 * destinataire sont bloquants : téléphone, TVA et langue se dégradent en "-".
 */
export function routeStopIssues (row) {
  const parsed = parseRouteAddress(row && row.addresses)
  return ROUTE_REQUIRED.filter((item) => !parsed[item.field]).map((item) => item.label)
}

/**
 * `cbc:StreetName` du XML UBL e-factura : « rue numéro, localité code_postal ».
 * Chaque segment est filtré — une pièce manquante raccourcit la chaîne, elle ne
 * la pollue jamais. Un `undefined` ici partirait tel quel à l'ANAF sur un
 * document fiscal.
 */
export function efacturaStreetName (address) {
  if (!address || typeof address !== 'object') return ''
  const part = (name) => clean(address[name])
  const street = [part('street'), part('nr')].filter(Boolean).join(' ')
  const city = [part('place'), part('cp')].filter(Boolean).join(' ')
  return [street, city].filter(Boolean).join(', ')
}

/**
 * Judeţe roumains → code ISO 3166-2 (sans préfixe « RO- »).
 *
 * Miroir de api/app/helpers/roJudete.js — api/ et src/ n'ont pas de build commun,
 * si l'une bouge penser à l'autre. La table de référence en base est
 * `places.region_code` (migration 042) ; ceci n'est plus qu'un filet pour les
 * lignes non encore migrées.
 */
const JUDET_CODES = {
  'bucuresti': 'B', 'alba': 'AB', 'arad': 'AR', 'arges': 'AG', 'bacau': 'BC',
  'bihor': 'BH', 'bistrita-nasaud': 'BN', 'bistrita nasaud': 'BN', 'botosani': 'BT',
  'braila': 'BR', 'brasov': 'BV', 'buzau': 'BZ', 'calarasi': 'CL',
  'caras-severin': 'CS', 'caras severin': 'CS', 'cluj': 'CJ', 'constanta': 'CT',
  'covasna': 'CV', 'dambovita': 'DB', 'dolj': 'DJ', 'galati': 'GL',
  'giurgiu': 'GR', 'gorj': 'GJ', 'harghita': 'HR', 'hunedoara': 'HD',
  'ialomita': 'IL', 'iasi': 'IS', 'ilfov': 'IF', 'maramures': 'MM',
  'mehedinti': 'MH', 'mures': 'MS', 'neamt': 'NT', 'olt': 'OT',
  'prahova': 'PH', 'salaj': 'SJ', 'satu mare': 'SM', 'satu-mare': 'SM',
  'sibiu': 'SB', 'suceava': 'SV', 'teleorman': 'TR', 'timis': 'TM',
  'tulcea': 'TL', 'valcea': 'VL', 'vaslui': 'VS', 'vrancea': 'VN'
}

const CODE_SET = Object.keys(JUDET_CODES).reduce((acc, key) => {
  acc[JUDET_CODES[key]] = true
  return acc
}, {})

/**
 * Repli diacritique roumain — miroir de api/app/helpers/roFold.js.
 * NFD ne décompose PAS ş/ţ (cédille, U+015F/U+0163) ni ș/ț (virgule souscrite,
 * U+0219/U+021B) : sans ce mapping explicite, « Timiş » (tel que stocké) et
 * « Timiș » (tel que tapé) restent deux chaînes différentes.
 */
const RO_FOLD = { 'ă': 'a', 'â': 'a', 'î': 'i', 'ș': 's', 'ş': 's', 'ț': 't', 'ţ': 't' }

function foldRo (value) {
  return clean(value)
    .toLowerCase()
    .replace(/[ăâîșşțţ]/g, (c) => RO_FOLD[c] || c)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
}

/**
 * Nom de judeţ (ou code déjà résolu) → code ISO sans préfixe. null si inconnu.
 * Insensible aux diacritiques et à la casse — c'est tout l'intérêt : la table à
 * clés exactes des écrans ratait « Timiș » quand la base stockait « Timiş ».
 */
export function judetCode (regionOrCode) {
  const raw = clean(regionOrCode)
  if (!raw) return null
  const asCode = raw.replace(/^RO[-\s]?/i, '').toUpperCase()
  if (CODE_SET[asCode]) return asCode
  const folded = foldRo(raw).replace(/^jud(?:e[tț]?ul)?\.?\s*/, '').trim()
  return JUDET_CODES[folded] || null
}

/**
 * `cbc:CountrySubentity` roumain : « RO-<code du judet> ».
 *
 * Ordre de résolution, du plus sûr au moins sûr :
 *   1. `address.region_code` — la colonne `places.region_code` (migration 042),
 *      seule donnée qui n'est pas une déduction ;
 *   2. `resolve` (getJudetAbbreviation de l'écran), pour ne rien casser ;
 *   3. la table repliée ci-dessus, qui rattrape les variantes d'encodage.
 *
 * Renvoie '' quand rien ne résout. NE JAMAIS y mettre un judeţ par défaut :
 * l'ancien `|| "HD"` de l'écran firewood envoyait Hunedoara à l'ANAF pour toute
 * région dont l'orthographe ne tombait pas pile sur la clé — un judeţ faux sur un
 * document fiscal, sans le moindre signal. Une valeur vide fait échouer l'envoi,
 * ce qui est visible et corrigeable ; une valeur fausse, non.
 */
export function efacturaCountrySubentity (address, resolve) {
  const stored = address ? clean(address.region_code) : ''
  if (stored) return `RO-${stored.replace(/^RO[-\s]?/i, '').toUpperCase()}`

  const region = address ? clean(address.region) : ''
  if (!region) return ''

  const fromScreen = typeof resolve === 'function' ? resolve(region) : null
  const code = clean(fromScreen) || judetCode(region)
  return code ? `RO-${code}` : ''
}

/**
 * Écrit le bloc d'adresse dans un document jsPDF, en respectant l'interligne
 * historique de 3 mm à partir de `y`. Renvoie false si rien n'a été écrit,
 * pour que l'appelant imprime son "n/a".
 */
export function drawAddressBlock (doc, address, x, y, lineHeight = 3) {
  const lines = addressLines(address)
  if (!lines.length) return false
  lines.forEach((line, index) => doc.text(x, y + (index * lineHeight), line))
  return true
}

// Libellé « adresse manquante », nommant l'adresse réellement en cause.
//
// Il existait en six copies : quatre `addressMissingMessage` (les quatre
// OperationView) qui ne disaient pas LAQUELLE manque, et deux
// `addressSendBlockMessage` (devis admin + broker) qui le disaient bien.
//
// EN ANGLAIS, comme le reste de l'interface. Les anciennes copies traduisaient
// dans la langue du CLIENT, alors que ce texte s'affiche en notification a
// l'operateur de l'ERP : un client roumain faisait apparaitre un message
// roumain a un operateur qui ne le lit pas.
const MISSING_SUBJECT = {
  billing: 'The billing address is missing',
  delivery: 'The delivery address is missing',
  both: 'The billing and delivery addresses are missing'
}

// `send` : le document part chez le client. `document` : on le fabrique.
const MISSING_TAIL = {
  send: ' \u2014 the document cannot be sent to the client. Please set the address on the client record, then try again.',
  document: ' \u2014 please set it on the operation, then restart the generation.'
}

/**
 * @param {'billing'|'delivery'|'both'} which    quelle adresse manque
 * @param {'document'|'send'} [purpose]          fabrication ou envoi
 */
export function addressMissingText (which, purpose = 'document') {
  return (MISSING_SUBJECT[which] || MISSING_SUBJECT.both) +
    (MISSING_TAIL[purpose] || MISSING_TAIL.document)
}

/* ------------------------------------------------------------------------ *
 * Bandeau « No address » des vues devis
 * ------------------------------------------------------------------------ */

/**
 * Résumé d'une liste d'options d'adresse (/person/getaddressbilling|delivery/
 * :idperson) : combien de candidates, combien marquées par défaut. `null`
 * quand la liste n'a pas pu être lue — on ne conclut rien d'un échec réseau.
 */
export function summarizeAddressOptions (list) {
  if (!Array.isArray(list)) return null
  const valid = list.filter((o) => o && o.value !== null && o.value !== undefined)
  const defaults = valid.filter((o) => o.isdefault === true || o.isdefault === 'true').length
  return { count: valid.length, defaults }
}

/**
 * Résumé des adresses de la FICHE client (/people_addresses/:idperson), toutes
 * catégories confondues : 1 = les deux, 2 = facturation, 3 = livraison.
 * Sert à ne jamais afficher « aucune adresse » devant une fiche qui en montre
 * une — mal catégorisée, elle n'est proposée dans aucune liste.
 */
export function summarizeClientAddresses (list) {
  if (!Array.isArray(list)) return null
  const categoryOf = (a) => {
    const raw = a.idaddresscategory || (a.addresscategory && a.addresscategory.id)
    return raw ? Number(raw) : null
  }
  const cats = list.filter(Boolean).map(categoryOf)
  return {
    total: cats.length,
    billing: cats.filter((c) => c === 1 || c === 2).length,
    delivery: cats.filter((c) => c === 1 || c === 3).length,
    uncategorised: cats.filter((c) => c === null).length
  }
}

/**
 * Ce qu'il faut faire pour une adresse manquante, et OÙ.
 *
 * L'ancien bandeau disait « Missing Billing Address or Missing Delivery
 * Address » avec un seul lien, vers la fiche client. Or la fiche client ne lie
 * jamais une adresse à un devis : l'utilisateur corrigeait la fiche, revenait,
 * et le bandeau restait (signalement récurrent des brokers). Depuis, le serveur
 * lie lui-même la seule adresse du contact ou celle par défaut
 * (api/app/helpers/addressLink.js) ; ce qui reste à afficher ici est donc soit
 * un contact SANS adresse (à créer sur la fiche), soit un choix à faire SUR LE
 * DEVIS.
 *
 * @param {'billing'|'delivery'} slot
 * @param {{count:number, defaults:number}|null} summary  cf. summarizeAddressOptions
 * @param {string} [clientName]
 * @param {{linkedEmpty?: boolean, client?: object|null}} [context]
 *   linkedEmpty : un lien existe mais l'adresse liée est vide ;
 *   client      : cf. summarizeClientAddresses (adresses de la fiche).
 * @returns {{ slot: string, text: string, actions: string[] }}  actions ⊆ ['quotation','client']
 */
export function addressMissingAdvice (slot, summary, clientName, context) {
  const kind = slot === 'billing' ? 'billing' : 'delivery'
  const who = clean(clientName) ? `The client "${clean(clientName)}"` : 'The client'
  const ctx = context || {}
  if (ctx.linkedEmpty) {
    return {
      slot,
      actions: ['client', 'quotation'],
      text: `The ${kind} address linked to this quotation is empty (no street, no city, no postal code). Complete it on the client record, or choose another one on the quotation.`
    }
  }
  if (!summary) {
    return {
      slot,
      actions: ['quotation', 'client'],
      text: `No ${kind} address is linked to this quotation, and the client's addresses could not be checked. Choose one on the quotation, or add one on the client record.`
    }
  }
  if (summary.count === 0) {
    // Le contact A des adresses, mais aucune n'est proposable ici : catégorie
    // « livraison seule » pour la facturation (ou l'inverse), ou pas de
    // catégorie du tout. Dire « aucune adresse » serait faux — c'est exactement
    // ce qui fait dire « il y a une adresse et elle ne charge pas ».
    if (ctx.client && ctx.client.total > 0) {
      const n = ctx.client.total
      const label = kind === 'billing' ? 'Billing' : 'Delivery'
      return {
        slot,
        actions: ['client'],
        text: `${who} has ${n} address${n > 1 ? 'es' : ''} on record, but none is categorised for ${kind} (category "Both" or "${label}"). On the client record, change the category of the right address: it will then be linked to this quotation automatically.`
      }
    }
    return {
      slot,
      actions: ['client'],
      text: `${who} has no ${kind} address on record. Add it on the client record: it will be linked to this quotation automatically.`
    }
  }
  if (summary.count === 1) {
    return {
      slot,
      actions: ['quotation'],
      text: `${who} has one ${kind} address but it is not linked to this quotation. Open the quotation, check the address and save.`
    }
  }
  let def = `${summary.defaults} are marked as default`
  if (summary.defaults === 0) def = 'none is marked as default'
  return {
    slot,
    actions: ['quotation', 'client'],
    text: `${who} has ${summary.count} ${kind} addresses and ${def}. Choose one on the quotation, or mark a single one as default on the client record.`
  }
}

export default {
  normalizeAddress,
  hasAddress,
  addressLines,
  addressOneLine,
  drawAddressBlock,
  addressMissingText,
  summarizeAddressOptions,
  summarizeClientAddresses,
  addressMissingAdvice,
  PARTS
}
